import { useState, type FormEvent } from "react";
import { createMerchant, ApiError, type MerchantWithCredentials } from "@/lib/api";
import { Copy, CheckCircle2, AlertTriangle, KeyRound, Mail, Link2 } from "lucide-react";
import { Modal } from "@/components/Modal";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (merchant: MerchantWithCredentials) => void;
};

export function MerchantCreateModal({ open, onClose, onCreated }: Props) {
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("CD");
  const [livemode, setLivemode] = useState(false);
  const [mobileBps, setMobileBps] = useState("350");
  const [cardsBps, setCardsBps] = useState("450");
  const [refundDays, setRefundDays] = useState("90");
  const [ticketSliceFrom, setTicketSliceFrom] = useState("-6");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<MerchantWithCredentials | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const reset = () => {
    setBusinessName("");
    setEmail("");
    setPhone("");
    setCountry("CD");
    setLivemode(false);
    setMobileBps("350");
    setCardsBps("450");
    setRefundDays("90");
    setTicketSliceFrom("-6");
    setLoading(false);
    setError(null);
    setCreated(null);
    setCopied(null);
  };

  const handleClose = () => {
    if (loading) return;
    if (created && onCreated) onCreated(created);
    reset();
    onClose();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const sliceFromNum = parseInt(ticketSliceFrom, 10);
      const parsingRule = !Number.isNaN(sliceFromNum)
        ? { fields: [{ name: "ticket_number", type: "slice", from: sliceFromNum }] }
        : undefined;
      const result = await createMerchant({
        business_name: businessName.trim(),
        business_email: email.trim(),
        business_phone: phone.trim() || undefined,
        country: country.trim().toUpperCase(),
        livemode,
        commission_rate_mobile_bps: parseInt(mobileBps, 10),
        commission_rate_cards_bps: parseInt(cardsBps, 10),
        refund_window_days: parseInt(refundDays, 10),
        reference_parsing_rule: parsingRule,
      });
      setCreated(result);
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError("Erreur réseau.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (created) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        title="Marchand créé"
        subtitle={created.merchant.business_name}
        size="lg"
        closeOnOverlay={false}
        footer={
          <button onClick={handleClose} className="btn btn-primary btn-sm">
            Terminer
          </button>
        }
      >
        <div className="space-y-5">
          <div className="rounded-lg bg-success-50 border-l-4 border-success-500 px-4 py-3 flex items-center gap-3">
            <CheckCircle2 size={20} className="text-success-700 shrink-0" />
            <div>
              <div className="font-bold text-success-700 text-sm">Marchand créé avec succès</div>
              <div className="text-xs text-success-700 font-mono">{created.merchant.id}</div>
            </div>
          </div>

          <div className="rounded-lg bg-warning-50 border-l-4 border-warning-500 px-4 py-3 flex items-center gap-3">
            <AlertTriangle size={20} className="text-warning-700 shrink-0" />
            <div>
              <div className="font-bold text-warning-700 text-sm">Credentials affichés une seule fois</div>
              <div className="text-xs text-warning-700 mt-0.5">{created.warning}</div>
            </div>
          </div>

          {created.dashboard_login && (
            <div className="rounded-xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-brand-500 text-white flex items-center justify-center">
                  <KeyRound size={16} />
                </div>
                <div>
                  <div className="font-bold text-[#181c32] text-sm">Compte dashboard marchand</div>
                  <div className="text-xs text-[#7e8299]">À transmettre au marchand pour sa première connexion</div>
                </div>
              </div>
              <div className="space-y-2">
                <CredRow
                  icon={<Mail size={14} />}
                  label="Email"
                  value={created.dashboard_login.email}
                  copyKey="dashboard_email"
                  copied={copied}
                  onCopy={copy}
                />
                <CredRow
                  icon={<KeyRound size={14} />}
                  label="Mot de passe temporaire"
                  value={created.dashboard_login.temp_password}
                  copyKey="dashboard_pwd"
                  copied={copied}
                  onCopy={copy}
                />
                <CredRow
                  icon={<Link2 size={14} />}
                  label="URL de connexion"
                  value={created.dashboard_login.login_url}
                  copyKey="dashboard_url"
                  copied={copied}
                  onCopy={copy}
                />
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-3">Credentials API</div>
            <div className="space-y-3">
              {[
                { key: "api_key", label: "API Key (publique)", value: created.api_key },
                { key: "api_secret", label: "API Secret", value: created.api_secret },
                { key: "hmac_callback_secret", label: "HMAC Callback Secret", value: created.hmac_callback_secret },
              ].map((c) => (
                <div key={c.key}>
                  <div className="form-label">{c.label}</div>
                  <div className="flex items-stretch gap-2">
                    <code className="flex-1 px-3 py-2.5 bg-[#f5f8fa] rounded-lg text-xs font-mono break-all">{c.value}</code>
                    <button onClick={() => copy(c.key, c.value)} className="btn btn-light-primary btn-sm shrink-0">
                      {copied === c.key ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                      {copied === c.key ? "Copié" : "Copier"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Nouveau marchand"
      subtitle="Informations business et configuration technique"
      size="lg"
      closeOnOverlay={!loading}
      footer={
        <>
          <button type="button" onClick={handleClose} disabled={loading} className="btn btn-light btn-sm">
            Annuler
          </button>
          <button type="submit" form="merchant-create-form" disabled={loading} className="btn btn-primary btn-sm">
            {loading ? "Création…" : "Créer le marchand"}
          </button>
        </>
      }
    >
      <form id="merchant-create-form" onSubmit={onSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Nom commercial *</label>
            <input
              required
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="form-input"
              placeholder="Ex: Compagnie Africaine d'Aviation"
            />
          </div>
          <div>
            <label className="form-label">Email contact *</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder="tech@example-air.com"
            />
          </div>
          <div>
            <label className="form-label">Téléphone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="form-input" placeholder="+243..." />
          </div>
          <div>
            <label className="form-label">Pays (ISO 2)</label>
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              maxLength={2}
              className="form-input uppercase"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Commission mobile (bps)</label>
            <input type="number" value={mobileBps} onChange={(e) => setMobileBps(e.target.value)} className="form-input" />
            <div className="form-hint">{(parseInt(mobileBps, 10) / 100).toFixed(2)}%</div>
          </div>
          <div>
            <label className="form-label">Commission cards (bps)</label>
            <input type="number" value={cardsBps} onChange={(e) => setCardsBps(e.target.value)} className="form-input" />
            <div className="form-hint">{(parseInt(cardsBps, 10) / 100).toFixed(2)}%</div>
          </div>
          <div>
            <label className="form-label">Refund window (jours)</label>
            <input type="number" value={refundDays} onChange={(e) => setRefundDays(e.target.value)} className="form-input" />
          </div>
        </div>

        <div>
          <label className="form-label">Parsing référence — slice from</label>
          <input
            value={ticketSliceFrom}
            onChange={(e) => setTicketSliceFrom(e.target.value)}
            className="form-input"
            placeholder="-6 = 6 derniers chars"
          />
          <div className="form-hint">
            Pour CAA: -6 (les 6 derniers caractères de la référence = numéro de billet)
          </div>
        </div>

        <label className="inline-flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={livemode} onChange={(e) => setLivemode(e.target.checked)} className="rounded" />
          <span className="text-sm text-[#3f4254]">
            Mode <span className="font-semibold">livemode</span> (production réelle, vrais paiements)
          </span>
        </label>

        {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      </form>
    </Modal>
  );
}

function CredRow({
  icon,
  label,
  value,
  copyKey,
  copied,
  onCopy,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  copyKey: string;
  copied: string | null;
  onCopy: (key: string, value: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-brand-100">
      <div className="w-7 h-7 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold">{label}</div>
        <div className="font-mono text-xs text-[#181c32] truncate">{value}</div>
      </div>
      <button
        onClick={() => onCopy(copyKey, value)}
        className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-[#7e8299] hover:bg-brand-50 hover:text-brand-700 transition-colors"
        aria-label={`Copier ${label}`}
      >
        {copied === copyKey ? <CheckCircle2 size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
